import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CarFront, FileText, Gavel, Sparkles, LogOut, KeyRound, Check, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { INPUT_CLS, ERROR_CLS } from '@/components/brand/BrandKit';
import { Field, PasswordField, startGoogleSignIn } from '@/components/account/AuthShell';
import { updateProfile, changePassword, apiError, ACCOUNT_TYPE_LABEL, STATUS_LABEL } from '@/api/account';

const CARD = 'rounded-2xl border border-black/5 bg-white p-6 sm:p-8 shadow-[0_16px_40px_rgba(0,0,0,0.05)]';
const STATUS_TONE = { active: 'bg-green-50 text-green-700 border-green-200', pending: 'bg-[#FFF4ED] text-[#EE5A01] border-[#EE5A01]/30', rejected: 'bg-red-50 text-red-700 border-red-200' };
const QUICK = [
  { label: 'Book a chauffeur or transfer', to: '/book-chauffeur', Icon: CarFront, testid: 'account-link-book' },
  { label: 'Request a corporate quote', to: '/contact', Icon: FileText, testid: 'account-link-quote' },
  { label: 'Used-car auction (B2B)', to: '/auction', Icon: Gavel, testid: 'account-link-auction' },
  { label: 'AI Assisted Car Rental', to: '/ai-car-rental', Icon: Sparkles, testid: 'account-link-ai' },
];

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

function CardTitle({ children, sub }) {
  return (<div className="mb-5"><h2 className="font-heading font-bold text-lg text-[#121212]">{children}</h2>{sub && <p className="mt-1 font-body text-sm text-[#666666]">{sub}</p>}</div>);
}

function Feedback({ ok, error, testid }) {
  if (error) return <p data-testid={`${testid}-error`} role="alert" className={ERROR_CLS}>{error}</p>;
  if (ok) return <p data-testid={`${testid}-success`} className="flex items-center gap-2 font-body text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3"><Check className="w-4 h-4" /> {ok}</p>;
  return null;
}

export function AccountHeader({ customer, onLogout, isNew }) {
  return (
    <section className="bg-[#121212] text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 flex flex-col sm:flex-row sm:items-center gap-6">
        {customer.picture
          ? <img src={customer.picture} alt="" referrerPolicy="no-referrer" className="h-16 w-16 rounded-full object-cover ring-2 ring-[#EE5A01]" />
          : <span data-testid="account-avatar" className="grid h-16 w-16 place-items-center rounded-full bg-[#EE5A01] font-heading font-black text-xl">{initials(customer.name)}</span>}
        <div className="flex-1 min-w-0">
          <p className="font-heading text-[11px] font-bold tracking-[0.15em] uppercase text-[#EE5A01]">{isNew ? 'Welcome aboard' : 'My Account'}</p>
          <h1 data-testid="account-name" className="mt-1 font-heading font-black text-3xl sm:text-4xl leading-tight truncate">{customer.name || customer.email}</h1>
          <p data-testid="account-email" className="mt-1 font-body text-sm text-white/70 truncate">{customer.email}{customer.company ? ` · ${customer.company}` : ''}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span data-testid="account-type-badge" className="rounded-full border border-white/20 px-3 py-1 font-heading text-[11px] font-bold uppercase tracking-wider">{ACCOUNT_TYPE_LABEL[customer.account_type] || customer.account_type}</span>
            <span data-testid="account-status-badge" className={`rounded-full border px-3 py-1 font-heading text-[11px] font-bold uppercase tracking-wider ${STATUS_TONE[customer.status] || ''}`}>{STATUS_LABEL[customer.status] || customer.status}</span>
          </div>
        </div>
        <button type="button" data-testid="account-logout" onClick={onLogout} className="inline-flex min-h-[44px] items-center gap-2 self-start rounded-full border border-white/25 px-5 font-heading font-bold text-sm hover:bg-white hover:text-[#121212] transition-colors"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </section>
  );
}

export function CompleteProfileNotice() {
  return (
    <div data-testid="account-complete-profile" className="flex items-start gap-3 rounded-2xl border border-[#EE5A01]/30 bg-[#FFF4ED] p-4 font-body text-sm text-[#7A2E00]">
      <AlertTriangle className="w-5 h-5 flex-shrink-0 text-[#EE5A01]" />
      <span>Add your mobile number so our team can reach you about bookings and quotes.</span>
    </div>
  );
}

export function ProfileCard({ customer, onSaved }) {
  const [form, setForm] = useState({ name: customer.name || '', mobile: customer.mobile || '', company: customer.company || '', job_title: customer.job_title || '' });
  const [state, setState] = useState({ ok: '', error: '', saving: false });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setState({ ok: '', error: '', saving: true });
    try {
      onSaved(await updateProfile(form));
      setState({ ok: 'Profile saved.', error: '', saving: false });
    } catch (err) {
      setState({ ok: '', error: apiError(err), saving: false });
    }
  };
  const locked = Boolean(customer.uaepass);
  return (
    <form onSubmit={save} data-testid="profile-card" className={CARD}>
      <CardTitle sub={locked ? 'Name and mobile are verified by UAE PASS and locked.' : 'Keep your contact details up to date.'}>Profile</CardTitle>
      <div className="space-y-4">
        <Field label="Full name" htmlFor="profile-name"><Input id="profile-name" data-testid="profile-name" value={form.name} onChange={set('name')} readOnly={locked} className={`${INPUT_CLS} ${locked ? 'bg-[#F5F5F3] text-[#888]' : ''}`} /></Field>
        <Field label="Mobile number" htmlFor="profile-mobile"><Input id="profile-mobile" data-testid="profile-mobile" type="tel" value={form.mobile} onChange={set('mobile')} readOnly={locked && Boolean(customer.mobile)} placeholder="+971 50 123 4567" className={`${INPUT_CLS} ${locked && customer.mobile ? 'bg-[#F5F5F3] text-[#888]' : ''}`} dir="ltr" /></Field>
        <Field label="Email" htmlFor="profile-email"><Input id="profile-email" data-testid="profile-email" value={customer.email} readOnly className={`${INPUT_CLS} bg-[#F5F5F3] text-[#888]`} /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Company" htmlFor="profile-company"><Input id="profile-company" data-testid="profile-company" value={form.company} onChange={set('company')} placeholder="Optional" className={INPUT_CLS} /></Field>
          <Field label="Job title" htmlFor="profile-job-title"><Input id="profile-job-title" data-testid="profile-job-title" value={form.job_title} onChange={set('job_title')} placeholder="Optional" className={INPUT_CLS} /></Field>
        </div>
        <Feedback ok={state.ok} error={state.error} testid="profile" />
        <button type="submit" data-testid="profile-save" disabled={state.saving} className="btn-primary disabled:opacity-50">{state.saving ? 'Saving…' : 'Save changes'}</button>
      </div>
    </form>
  );
}

export function SecurityCard({ customer, onSaved }) {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [state, setState] = useState({ ok: '', error: '', saving: false });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { setState({ ok: '', error: 'The two passwords do not match.', saving: false }); return; }
    setState({ ok: '', error: '', saving: true });
    try {
      await changePassword({ current_password: form.current_password, new_password: form.new_password });
      onSaved({ ...customer, has_password: true, providers: Array.from(new Set([...(customer.providers || []), 'password'])) });
      setForm({ current_password: '', new_password: '', confirm: '' });
      setState({ ok: customer.has_password ? 'Password changed.' : 'Password set. You can now sign in with email and password too.', error: '', saving: false });
    } catch (err) {
      setState({ ok: '', error: apiError(err), saving: false });
    }
  };
  return (
    <form onSubmit={save} data-testid="security-card" className={CARD}>
      <CardTitle sub={customer.has_password ? 'Use at least 8 characters with a letter and a number.' : 'You signed up with Google. Add a password to also sign in with email.'}>{customer.has_password ? 'Change password' : 'Set a password'}</CardTitle>
      <div className="space-y-4">
        {customer.has_password && <Field label="Current password" htmlFor="password-current"><PasswordField id="password-current" testid="password-current" value={form.current_password} onChange={set('current_password')} /></Field>}
        <Field label="New password" htmlFor="password-new"><PasswordField id="password-new" testid="password-new" value={form.new_password} onChange={set('new_password')} placeholder="New password" autoComplete="new-password" /></Field>
        <Field label="Confirm new password" htmlFor="password-confirm"><PasswordField id="password-confirm" testid="password-confirm" value={form.confirm} onChange={set('confirm')} placeholder="Repeat new password" autoComplete="new-password" /></Field>
        <Feedback ok={state.ok} error={state.error} testid="password" />
        <button type="submit" data-testid="password-save" disabled={state.saving || !form.new_password || !form.confirm || (customer.has_password && !form.current_password)} className="btn-secondary disabled:opacity-50"><KeyRound className="w-4 h-4" /> {state.saving ? 'Saving…' : customer.has_password ? 'Update password' : 'Set password'}</button>
      </div>
    </form>
  );
}

export function SignInMethodsCard({ customer }) {
  const hasGoogle = (customer.providers || []).includes('google');
  const Row = ({ label, on, action, testid }) => (
    <li data-testid={testid} className="flex items-center justify-between gap-3 py-3 border-b border-black/5 last:border-0">
      <span className="font-body text-sm text-[#121212]">{label}</span>
      {on ? <span className="inline-flex items-center gap-1 rounded-full bg-green-50 border border-green-200 px-2.5 py-0.5 font-heading text-[11px] font-bold text-green-700 uppercase tracking-wider"><Check className="w-3 h-3" /> Linked</span> : action}
    </li>
  );
  return (
    <div data-testid="signin-methods-card" className={CARD}>
      <CardTitle sub="Both methods open the same account.">Sign-in methods</CardTitle>
      <ul>
        <Row label="Email & password" on={customer.has_password} testid="method-password" action={<span className="font-body text-xs text-[#888]">Set a password below</span>} />
        <Row label="Google" on={hasGoogle} testid="method-google" action={<button type="button" data-testid="link-google-btn" onClick={startGoogleSignIn} className="font-heading font-bold text-sm text-[#EE5A01] hover:underline">Link Google</button>} />
        <Row label="UAE PASS" on={Boolean(customer.uaepass)} testid="method-uaepass" action={<span className="font-body text-xs text-[#888]">Verify above</span>} />
      </ul>
    </div>
  );
}

export function QuickLinks() {
  return (
    <div data-testid="account-quick-links" className={CARD}>
      <CardTitle sub="Pick up where you left off.">Quick links</CardTitle>
      <ul className="space-y-1">
        {QUICK.map(({ label, to, Icon, testid }) => (
          <li key={to}>
            <Link to={to} data-testid={testid} className="group flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-[#FFF4ED] transition-colors">
              <span className="grid h-9 w-9 place-items-center rounded-md bg-[#F5F5F3] text-[#EE5A01] group-hover:bg-white"><Icon className="w-4 h-4" /></span>
              <span className="flex-1 font-heading font-bold text-sm text-[#121212]">{label}</span>
              <ArrowRight className="w-4 h-4 text-[#999] group-hover:text-[#EE5A01] group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
